import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { GENERATION_TOOL, getToolCopy, getToolTitle } from 'src/data/generationTool';
import { PROJECT_TYPE } from 'src/data/project';

import { CardLarge } from './components/CardLarge';
import { CardMedium } from './components/CardMedium';
import { CardSmall } from './components/CardSmall';
import { customToolDataPropTypes } from './lib';

export const PROJECT_TEMPLATE_CARD_TYPE = {
  small: 'small',
  medium: 'medium',
  large: 'large',
};

const CARD_COMPONENT = {
  [PROJECT_TEMPLATE_CARD_TYPE.small]: CardSmall,
  [PROJECT_TEMPLATE_CARD_TYPE.medium]: CardMedium,
  [PROJECT_TEMPLATE_CARD_TYPE.large]: CardLarge,
};

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  cardType: PropTypes.oneOf(Object.values(PROJECT_TEMPLATE_CARD_TYPE)),
  projectType: PropTypes.oneOf(Object.values(PROJECT_TYPE)).isRequired,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)).isRequired,
  customToolData: customToolDataPropTypes,
  subTemplateData: PropTypes.shape(),
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  showComingSoonTag: PropTypes.bool,
  disabled: PropTypes.bool,
};

const defaultProps = {
  className: null,
  classes: {},
  cardType: PROJECT_TEMPLATE_CARD_TYPE.large,
  customToolData: null,
  selected: false,
  showComingSoonTag: false,
  disabled: false,
  subTemplateData: null,
};

export const ProjectTemplateCard = ({
  className,
  classes,
  cardType,
  projectType,
  generationTool,
  customToolData,
  subTemplateData,
  selected,
  onSelect,
  showComingSoonTag,
  disabled,
}) => {
  const title = useMemo(() => {
    if (customToolData?.title || customToolData?.name) {
      return customToolData.title || customToolData.name;
    }
    if (subTemplateData?.name) {
      return subTemplateData.name;
    }

    return getToolTitle(generationTool);
  }, [generationTool, customToolData, subTemplateData]);

  const copy = useMemo(() => {
    if (generationTool === GENERATION_TOOL.customMultiPrompt) {
      return customToolData.info ? customToolData.info : customToolData.description;
    } else {
      return subTemplateData?.description || getToolCopy(generationTool);
    }
  }, [generationTool, customToolData, subTemplateData]);

  const handleCardClick = useCallback(() => {
    onSelect({
      projectType,
      generationTool,
      customToolData,
      subTemplateData,
      subTemplateId: subTemplateData?.id,
    });
  }, [onSelect, projectType, generationTool, customToolData, subTemplateData]);

  const CardComponent = CARD_COMPONENT[cardType];
  return (
    <CardComponent
      className={className}
      classes={classes}
      projectType={projectType}
      generationTool={generationTool}
      customToolData={customToolData}
      title={title}
      copy={copy}
      selected={selected}
      onClick={handleCardClick}
      showComingSoonTag={showComingSoonTag}
      disabled={disabled}
    />
  );
};

ProjectTemplateCard.displayName = 'ProjectTemplateCard';
ProjectTemplateCard.propTypes = propTypes;
ProjectTemplateCard.defaultProps = defaultProps;
